/**
 * Incoming Receipts - sysadmin cross-tenant pregled.
 *
 * Faza 1 MVP: tenant picker + lista skeniranih racuna sa filterima + detail modal.
 * Faze 2-4: analytics, SEF match, settings, export (dodaju kasnije tabove).
 */

import { useCallback, useEffect, useState } from 'react';
import { FileText, AlertTriangle, RefreshCw, Search, X, Download, Send, CheckCircle2, XCircle } from 'lucide-react';
import { api } from '../../api/client';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

type TenantOption = { id: string; name: string; taxId: string; enabled: boolean | null };

type Receipt = {
  id: string;
  tenantId: string;
  locationId: string | null;
  sellerName: string;
  sellerPib: string;
  esirId: string | null;
  totalCounter: number | null;
  invoiceNumber: string;
  invoiceType: string | null;
  transactionType: string | null;
  issueDatetime: string;
  totalAmount: string;
  totalVat: string;
  totalNet: string;
  status: string;
  errorCode: string | null;
  createdAt: string;
  note: string | null;
  category: string | null;
};

type ReceiptItem = {
  id: string;
  lineNumber: number;
  name: string;
  quantity: string;
  unit: string | null;
  unitPrice: string;
  total: string;
  vatRate: string | null;
  vatLabel: string | null;
};

type ListResponse = { data: Receipt[]; page: number; pageSize: number; total: number };
type DeliveryLog = {
  id: string;
  channel: 'email' | 'webhook';
  target: string;
  status: 'sent' | 'failed' | 'pending';
  attempt: number;
  response: string | null;
  error: string | null;
  sentAt: string;
};

type DetailResponse = { receipt: Receipt; items: ReceiptItem[]; deliveries: DeliveryLog[] };

const statusColors: Record<string, { bg: string; color: string }> = {
  pending: { bg: 'rgba(156,163,175,0.2)', color: '#9ca3af' },
  validated: { bg: 'rgba(59,130,246,0.2)', color: '#60a5fa' },
  delivered: { bg: 'rgba(34,197,94,0.2)', color: '#4ade80' },
  reconciled: { bg: 'rgba(168,85,247,0.2)', color: '#c084fc' },
  failed: { bg: 'rgba(239,68,68,0.2)', color: '#fca5a5' },
  duplicate: { bg: 'rgba(234,179,8,0.2)', color: '#facc15' },
  voided: { bg: 'rgba(107,114,128,0.2)', color: '#9ca3af' },
};

const fmtAmount = (n: string | number) =>
  new Intl.NumberFormat('sr-Latn-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n));

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('sr-Latn-RS', { dateStyle: 'short', timeStyle: 'short' });
};

export function IncomingReceiptsPage() {
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailResponse | null>(null);

  useEffect(() => {
    api.get<TenantOption[]>('/admin/incoming-receipts/tenants-with-module').then((rows) => {
      setTenants(rows);
      if (rows.length > 0) setSelectedTenantId(rows[0].id);
    }).catch(() => {});
  }, []);

  const fetchReceipts = useCallback(async () => {
    if (!selectedTenantId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        tenantId: selectedTenantId,
        page: String(page),
        pageSize: String(pageSize),
      });
      if (status) params.set('status', status);
      if (search.trim()) params.set('sellerPib', search.trim());
      const res = await api.get<ListResponse>(`/admin/incoming-receipts?${params}`);
      setReceipts(res.data);
      setTotal(res.total);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri učitavanju računa');
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId, page, pageSize, status, search]);

  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  const openDetail = async (id: string) => {
    try {
      const res = await api.get<DetailResponse>(`/admin/incoming-receipts/${id}`);
      setDetail(res);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri učitavanju detalja');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            Ulazni računi
          </h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Skenirani fiskalni računi koje tenant prima kao kupac
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={fetchReceipts} style={btnSecondary}>
          <RefreshCw size={14} /> Osveži
        </button>
      </header>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={lbl}>Tenant:</label>
        <select value={selectedTenantId} onChange={(e) => { setSelectedTenantId(e.target.value); setPage(1); }} style={selectStyle}>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} {t.enabled === false ? '(isključen)' : ''}
            </option>
          ))}
        </select>

        <label style={lbl}>Status:</label>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">Svi</option>
          <option value="validated">Validirano</option>
          <option value="delivered">Dostavljeno</option>
          <option value="reconciled">Uparen sa SEF</option>
          <option value="failed">Greška</option>
          <option value="duplicate">Duplikat</option>
        </select>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <Search size={14} color="var(--color-text-muted)" />
          <input
            value={search}
            placeholder="PIB prodavca..."
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (setPage(1), fetchReceipts())}
            style={inputStyle}
          />
        </div>
      </div>

      {error && (
        <div style={errorBox}>
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Datum</th>
                <th style={th}>Prodavac</th>
                <th style={th}>PIB</th>
                <th style={th}>Broj računa</th>
                <th style={{ ...th, textAlign: 'right' }}>Iznos</th>
                <th style={{ ...th, textAlign: 'right' }}>PDV</th>
                <th style={th}>Tip</th>
                <th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: 'var(--color-text-muted)' }}>Učitavanje...</td></tr>
              ) : receipts.length === 0 ? (
                <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: 'var(--color-text-muted)' }}>
                  <FileText size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                  <div>Nema skeniranih računa za ovaj filter</div>
                </td></tr>
              ) : receipts.map((r) => {
                const sc = statusColors[r.status] ?? statusColors.pending;
                return (
                  <tr key={r.id} onClick={() => openDetail(r.id)} style={{ cursor: 'pointer', borderTop: '1px solid var(--color-border)' }}>
                    <td style={td}>{fmtDate(r.issueDatetime)}</td>
                    <td style={td}>{r.sellerName}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{r.sellerPib}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{r.invoiceNumber}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmtAmount(r.totalAmount)}</td>
                    <td style={{ ...td, textAlign: 'right', color: 'var(--color-text-muted)' }}>{fmtAmount(r.totalVat)}</td>
                    <td style={td}>
                      {r.transactionType === 'REFUND' ? (
                        <span style={{ color: '#fca5a5', fontSize: 12 }}>Povraćaj</span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>{r.invoiceType ?? '-'}</span>
                      )}
                    </td>
                    <td style={td}>
                      <span style={{ ...badge, background: sc.bg, color: sc.color }}>{r.status}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {total > pageSize && (
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: 'var(--color-text-muted)' }}>
          <div>Ukupno {total} racuna</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} style={btnSecondary}>Prethodna</button>
            <span style={{ padding: '6px 12px' }}>{page} / {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={btnSecondary}>Sledeća</button>
          </div>
        </div>
      )}

      {detail && <DetailDrawer detail={detail} onClose={() => setDetail(null)} onRefresh={() => openDetail(detail.receipt.id)} />}
    </div>
  );
}

function DetailDrawer({ detail, onClose, onRefresh }: { detail: DetailResponse; onClose: () => void; onRefresh: () => void }) {
  useEscapeToClose(onClose);
  const { receipt, items, deliveries } = detail;
  const [resending, setResending] = useState(false);

  const downloadArtefact = (kind: 'pdf' | 'xml') => {
    // admin ruta `/api/admin/incoming-receipts/:id/pdf` vraca fajl
    // otvaramo u novom tab-u - browser hvata attachment header
    const token = localStorage.getItem('admin-token');
    fetch(`/api/admin/incoming-receipts/${receipt.id}/${kind}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${receipt.invoiceNumber.replace(/[^a-zA-Z0-9_-]+/g, '_')}.${kind}`;
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post(`/admin/incoming-receipts/${receipt.id}/resend`);
      onRefresh();
    } finally {
      setResending(false);
    }
  };

  return (
    <div style={drawerBackdrop}>
      <div onClick={(e) => e.stopPropagation()} style={drawer}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{receipt.sellerName}</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>PIB {receipt.sellerPib} · {fmtDate(receipt.issueDatetime)}</div>
          </div>
          <button onClick={onClose} style={iconBtn}><X size={16} /></button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <button onClick={() => downloadArtefact('pdf')} style={actionBtn}>
            <Download size={13} /> PDF
          </button>
          <button onClick={() => downloadArtefact('xml')} style={actionBtn}>
            <Download size={13} /> UBL XML
          </button>
          <button onClick={resend} disabled={resending} style={{ ...actionBtn, background: 'var(--color-primary)', color: '#fff' }}>
            <Send size={13} /> {resending ? 'Šalje...' : 'Pošalji ponovo'}
          </button>
        </div>

        <div style={gridKv}>
          <div><Kv label="Fiskalni broj" value={receipt.invoiceNumber} mono /></div>
          <div><Kv label="ESIR" value={receipt.esirId ? `${receipt.esirId} #${receipt.totalCounter}` : '-'} mono /></div>
          <div><Kv label="Tip" value={receipt.invoiceType ?? '-'} /></div>
          <div><Kv label="Transakcija" value={receipt.transactionType ?? '-'} /></div>
          <div><Kv label="Ukupno" value={fmtAmount(receipt.totalAmount) + ' RSD'} /></div>
          <div><Kv label="PDV" value={fmtAmount(receipt.totalVat) + ' RSD'} /></div>
          <div><Kv label="Neto" value={fmtAmount(receipt.totalNet) + ' RSD'} /></div>
          <div><Kv label="Status" value={receipt.status} /></div>
        </div>

        {items.length > 0 && (
          <>
            <div style={{ marginTop: 24, marginBottom: 8, fontSize: 13, fontWeight: 600, color: 'var(--color-text-muted)' }}>Stavke</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--color-bg)' }}>
                    <th style={{ ...th, padding: 8 }}>#</th>
                    <th style={{ ...th, padding: 8 }}>Naziv</th>
                    <th style={{ ...th, padding: 8, textAlign: 'right' }}>Kol</th>
                    <th style={{ ...th, padding: 8, textAlign: 'right' }}>Cena</th>
                    <th style={{ ...th, padding: 8, textAlign: 'right' }}>Total</th>
                    <th style={{ ...th, padding: 8 }}>PDV</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td style={{ ...td, padding: 8 }}>{it.lineNumber}</td>
                      <td style={{ ...td, padding: 8 }}>{it.name}</td>
                      <td style={{ ...td, padding: 8, textAlign: 'right' }}>{it.quantity} {it.unit ?? ''}</td>
                      <td style={{ ...td, padding: 8, textAlign: 'right' }}>{fmtAmount(it.unitPrice)}</td>
                      <td style={{ ...td, padding: 8, textAlign: 'right', fontWeight: 600 }}>{fmtAmount(it.total)}</td>
                      <td style={{ ...td, padding: 8 }}>{it.vatLabel} {it.vatRate ? `${it.vatRate}%` : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {receipt.note && (
          <div style={{ marginTop: 16, padding: 12, background: 'var(--color-bg)', borderRadius: 6, fontSize: 13 }}>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>Napomena</div>
            {receipt.note}
          </div>
        )}

        {deliveries.length > 0 && (
          <>
            <div style={{ marginTop: 24, marginBottom: 8, fontSize: 13, fontWeight: 600, color: 'var(--color-text-muted)' }}>Isporuka</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {deliveries.map((d) => (
                <div key={d.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  background: 'var(--color-bg)', borderRadius: 6, fontSize: 12,
                }}>
                  {d.status === 'sent' ? <CheckCircle2 size={14} color="#4ade80" /> : <XCircle size={14} color="#fca5a5" />}
                  <span style={{ fontWeight: 500, minWidth: 70 }}>{d.channel}</span>
                  <span style={{ flex: 1, color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.target}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                    attempt {d.attempt} · {fmtDate(d.sentAt)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Kv({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontFamily: mono ? 'monospace' : undefined }}>{value}</div>
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '12px 16px', textAlign: 'left', fontSize: 11,
  fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5,
};
const td: React.CSSProperties = { padding: '12px 16px', fontSize: 13, color: 'var(--color-text)' };
const lbl: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 500 };
const selectStyle: React.CSSProperties = {
  padding: '6px 10px', fontSize: 13, background: 'var(--color-bg-surface)',
  border: '1px solid var(--color-border)', borderRadius: 6, color: 'var(--color-text)',
};
const inputStyle: React.CSSProperties = { ...selectStyle, width: 180 };
const btnSecondary: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  fontSize: 13, fontWeight: 500, background: 'var(--color-bg-surface)',
  border: '1px solid var(--color-border)', borderRadius: 6, color: 'var(--color-text)', cursor: 'pointer',
};
const actionBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px',
  fontSize: 12, fontWeight: 500, background: 'var(--color-bg)',
  border: '1px solid var(--color-border)', borderRadius: 6, color: 'var(--color-text)', cursor: 'pointer',
};
const iconBtn: React.CSSProperties = {
  padding: 6, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)',
};
const badge: React.CSSProperties = { display: 'inline-block', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 };
const errorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: 12, marginBottom: 16,
  background: 'rgba(239,68,68,0.1)', color: '#fca5a5', borderRadius: 6, fontSize: 13,
};
const drawerBackdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', justifyContent: 'flex-end', zIndex: 100,
};
const drawer: React.CSSProperties = {
  width: '40%', minWidth: 'min(480px, 100vw)', maxWidth: 720, background: 'var(--color-bg-surface)',
  padding: 24, overflowY: 'auto', borderLeft: '1px solid var(--color-border)',
};
const gridKv: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 };
